import { BracketNodeBase } from "./BracketGraphBase";
import { BracketMatch, isSeedSource } from "./BracketMatch";
import { numberToLetter } from "Utility";
import { BracketGraph } from "./BracketGraph";
import { BracketType } from "model/Tournament/Segment/Bracket/Bracket";

export type ShuffleBracketGraph = {
  finalNodes: ShuffleBracketNode[];
};

export type ShuffleBracketSource = {
  node: ShuffleBracketNode | undefined;
  position: number;
  source: string;
};

export type ShuffleBracketNode = BracketNodeBase & {
  depth: number;
  isLower: boolean;
  match: BracketMatch;
  teamSources: ShuffleBracketSource[];
  matchTitle?: string;
};

export const isShuffleBracketNode = (
  node: BracketNodeBase
): node is ShuffleBracketNode => {
  return (node as ShuffleBracketNode).teamSources !== undefined;
};

export const isShuffleBracketGraph = (
  graph: ShuffleBracketGraph | BracketGraph
): graph is ShuffleBracketGraph => {
  return (graph as ShuffleBracketGraph).finalNodes[0].teamSources !== undefined;
};

export const getAllNodes = (nodes: ShuffleBracketNode[]) => {
  const allNodes: ShuffleBracketNode[] = [];
  nodes.forEach((n) => addAllChildrenNodesAndSelfRecursive(n, allNodes));
  return allNodes;
};

export const getMaxDepth = (nodes: ShuffleBracketNode[]) => {
  return Math.max(...nodes.map((n) => n.depth));
};

export const getMaxLowerDepth = (graph: ShuffleBracketGraph) => {
  const nodes = getAllNodes(graph.finalNodes).filter((n) => n.isLower);
  return getMaxDepth(nodes);
};

export const getMaxUpperDepth = (graph: ShuffleBracketGraph) => {
  const nodes = getAllNodes(graph.finalNodes).filter((n) => !n.isLower);
  return getMaxDepth(nodes);
};

export const shuffleBracketGraphHasGrandFinal = (
  graph: ShuffleBracketGraph
) => {
  return graph.finalNodes.length === 1;
};

export const getShuffleBracketGraphMatchTitle = (
  graph: ShuffleBracketGraph,
  bracketName: string | undefined,
  matchId: string
) => {
  const allNodes = getAllNodes(graph.finalNodes);
  const node = allNodes.find((n) => n.match.matchId === matchId);
  if (!node) {
    return undefined;
  }

  return `${!!bracketName ? `${bracketName}` : ""} ${node.matchTitle}`;
};

// Note - not explicitly ordered
const addAllChildrenNodesAndSelfRecursive = (
  node: ShuffleBracketNode,
  nodeList: ShuffleBracketNode[]
) => {
  if (nodeList.includes(node)) {
    return;
  }

  nodeList.push(node);
  node.teamSources.forEach((source) => {
    if (!!source.node) {
      addAllChildrenNodesAndSelfRecursive(source.node, nodeList);
    }
  });
};

export const buildShuffleBracketGraphAndAssignMatchTitles = (
  matches: BracketMatch[],
  bracketType: BracketType
) => {
  const graph = buildShuffleBracketGraph(matches, bracketType);
  assignMatchTitles(graph, bracketType);
  return graph;
};

const buildShuffleBracketGraph = (
  matches: BracketMatch[],
  bracketType: BracketType
): ShuffleBracketGraph | undefined => {
  const finalMatches = matches.filter((m) => m.isFinal);
  if (finalMatches.length === 0) {
    return undefined;
  }

  if (bracketType === "SingleElimShuffle" || bracketType === "Multilevel") {
    const existingNodes: ShuffleBracketNode[] = [];
    const finalNodes: ShuffleBracketNode[] = [];
    finalMatches.forEach((m) => {
      const finalNode = createBracketNodeRecursive(
        m,
        matches,
        false,
        1,
        existingNodes
      );
      finalNodes.push(finalNode);
      existingNodes.push(...getAllNodes([finalNode]));
    });

    return {
      finalNodes,
    };
  }

  if (bracketType === "DoubleElimShuffle") {
    if (finalMatches.length === 1) {
      const finalMatch = finalMatches[0];
      const teamsPerMatch = finalMatch.teamSources.length;
      const upperFinalTeamSources = finalMatch.teamSources.slice(
        0,
        teamsPerMatch / 2
      );
      const lowerFinalTeamSources = finalMatch.teamSources.filter(
        (s) => !upperFinalTeamSources.includes(s)
      );

      const upperFinalSources: ShuffleBracketSource[] = [];
      const lowerFinalSources: ShuffleBracketSource[] = [];

      const existingNodes: ShuffleBracketNode[] = [];
      upperFinalTeamSources.forEach((upperSource) => {
        if (isSeedSource(upperSource)) {
          upperFinalSources.push({
            node: undefined,
            position: upperSource.position,
            source: upperSource.source,
          });
        } else {
          const match = matches.find((m) => m.matchId === upperSource.source);
          if (!!match) {
            var node = existingNodes.find((n) => n.match === match);
            if (!node) {
              node = createBracketNodeRecursive(
                match,
                matches,
                false,
                2,
                existingNodes
              );
              existingNodes.push(node);
            }

            upperFinalSources.push({
              node,
              position: upperSource.position,
              source: upperSource.source,
            });
          }
        }
      });

      lowerFinalTeamSources.forEach((lowerSource) => {
        if (isSeedSource(lowerSource)) {
          lowerFinalSources.push({
            node: undefined,
            position: lowerSource.position,
            source: lowerSource.source,
          });
        } else {
          const match = matches.find((m) => m.matchId === lowerSource.source);
          if (!!match) {
            var node = existingNodes.find((n) => n.match === match);
            if (!node) {
              node = createBracketNodeRecursive(
                match,
                matches,
                true,
                2,
                existingNodes
              );
              existingNodes.push(node);
            }

            lowerFinalSources.push({
              node,
              position: lowerSource.position,
              source: lowerSource.source,
            });
          }
        }
      });

      const grandFinalNode: ShuffleBracketNode = {
        isLower: false,
        match: finalMatch,
        teamSources: upperFinalSources.concat(lowerFinalSources),
        depth: 1,
      };

      return { finalNodes: [grandFinalNode] };
    }

    const finalNodes: ShuffleBracketNode[] = [];
    const existingNodes: ShuffleBracketNode[] = [];
    const upperFinalMatches = finalMatches.filter((m) => m.isUpperFinal);
    upperFinalMatches.forEach((upperFinalMatch) => {
      const finalNode = createBracketNodeRecursive(
        upperFinalMatch,
        matches,
        false,
        1,
        existingNodes
      );
      existingNodes.push(finalNode);
      finalNodes.push(finalNode);
    });

    const lowerFinalMatches = finalMatches.filter((m) => !m.isUpperFinal);
    lowerFinalMatches.forEach((lowerFinalMatch) => {
      const finalNode = createBracketNodeRecursive(
        lowerFinalMatch,
        matches,
        true,
        1,
        existingNodes
      );
      finalNodes.push(finalNode);
    });

    return { finalNodes };
  }

  return undefined;
};

const createBracketNodeRecursive = (
  match: BracketMatch,
  matches: BracketMatch[],
  isLower: boolean,
  depth: number,
  existingNodes: ShuffleBracketNode[] = []
): ShuffleBracketNode => {
  const node: ShuffleBracketNode = {
    isLower,
    match,
    depth,
    teamSources: [],
  };

  match.teamSources.forEach((source) => {
    if (isSeedSource(source)) {
      node.teamSources.push({
        node: undefined,
        position: source.position,
        source: source.source,
      });
    } else {
      const sourceMatch = matches.find((m) => m.matchId === source.source);

      if (!!sourceMatch) {
        var existingNode = existingNodes.find((n) => n.match === sourceMatch);

        if (!existingNode) {
          existingNode = createBracketNodeRecursive(
            sourceMatch,
            matches,
            isLower,
            depth + 1,
            existingNodes
          );

          existingNodes.push(existingNode);
        }

        node.teamSources.push({
          node: existingNode,
          position: source.position,
          source: source.source,
        });
      }
    }
  });

  return node;
};

export const getNodesByDepth = (
  firstNodes: ShuffleBracketNode[],
  isLower: boolean
) => {
  const nodesByDepth: ShuffleBracketNode[][] = [];
  firstNodes.forEach((n) => {
    addNodeRecursive(n, nodesByDepth, isLower, 0);
  });

  nodesByDepth.forEach((nodes) =>
    nodes.sort((a, b) => a.match.matchId.localeCompare(b.match.matchId))
  );

  return nodesByDepth;
};

// todo - needs to deal with Single Elim / GFs better
export const addNodeRecursive = (
  node: ShuffleBracketNode,
  nodesByDepth: ShuffleBracketNode[][],
  isLower: boolean,
  depth: number
) => {
  const currentNodes = nodesByDepth.flat();
  if (currentNodes.includes(node)) {
    return;
  }

  if (node.isLower !== isLower) {
    return;
  }

  if (!nodesByDepth[depth]) {
    nodesByDepth[depth] = [];
  }

  nodesByDepth[depth].push(node);
  node.teamSources.forEach((source) => {
    if (!!source.node) {
      addNodeRecursive(source.node, nodesByDepth, isLower, depth + 1);
    }
  });
};

export const depthToRoundTitleDoubleElim = (
  depth: number,
  maxDepth: number,
  isLower: boolean,
  hasGrandFinal: boolean,
  hasMultipleFinals: boolean
) => {
  if (depth === 0 && hasGrandFinal) {
    return "Grand Final";
  }

  const adjustedMaxDepth = maxDepth - (hasGrandFinal ? 1 : 0);
  const roundTitle = depthToRoundTitleSingleElim(
    depth,
    adjustedMaxDepth,
    hasMultipleFinals
  );
  return `${isLower ? "Lower " : "Upper "} ${roundTitle}`;
};

export const depthToRoundTitleSingleElim = (
  depth: number,
  maxDepth: number,
  hasMultipleFinals: boolean
) => {
  switch (depth) {
    case 1:
      return `Final${hasMultipleFinals ? "s" : ""}`;
    case 2:
      return "Semi-finals";
    case 3:
      return "Quarter-finals";
    default:
      return `Round ${maxDepth - depth + 1}`;
  }
};

export const depthToMatchTitleDoubleElim = (
  depth: number,
  maxDepth: number,
  isLower: boolean,
  hasGrandFinal: boolean,
  count: number
) => {
  if (depth === 1 && hasGrandFinal) {
    return `Grand Final - ${numberToLetter(count)}`;
  }

  const adjustedDepth = depth - (hasGrandFinal ? 1 : 0);
  const adjustedMaxDepth = maxDepth - (hasGrandFinal ? 1 : 0);
  const roundTitle = depthToMatchTitleSingleElim(
    adjustedDepth,
    adjustedMaxDepth
  );

  return `${isLower ? "Lower " : "Upper "} ${roundTitle} - ${numberToLetter(
    count
  )}`;
};

export const depthToMatchTitleSingleElim = (
  depth: number,
  maxDepth: number,
  count?: number
) => {
  var roundTitle = "";
  switch (depth) {
    case 1:
      roundTitle = "Final";
      break;
    case 2:
      roundTitle = "SF";
      break;
    case 3:
      roundTitle = "QF";
      break;
    default:
      roundTitle = `Round ${maxDepth - depth + 1}`;
      break;
  }

  return !!count ? `${roundTitle} - ${numberToLetter(count)}` : roundTitle;
};

export const getDoubleElimNodesByDepth = (firstNodes: ShuffleBracketNode[]) => {
  var upperNodesByDepth: ShuffleBracketNode[][] = [];
  var lowerNodesByDepth: ShuffleBracketNode[][] = [];

  firstNodes.forEach((n) => {
    const upperSourceNodes = firstNodes.filter((n) => !n.isLower);
    const lowerSourceNodes = firstNodes.filter((n) => n.isLower);
    upperNodesByDepth = getNodesByDepth(upperSourceNodes, false);
    lowerNodesByDepth = getNodesByDepth(lowerSourceNodes, true);
  });

  return {
    upperNodesByDepth,
    lowerNodesByDepth,
  };
};

const assignMatchTitles = (
  graph: ShuffleBracketGraph | undefined,
  bracketType: BracketType
) => {
  if (!graph) {
    return;
  }

  if (bracketType === "SingleElimShuffle" || bracketType === "Multilevel") {
    const nodesByDepth = getNodesByDepth(graph.finalNodes, false);
    const maxDepth = getMaxDepth(nodesByDepth.flat());
    let count = 1;
    nodesByDepth.reverse().forEach((d) => {
      d.forEach((n) => {
        n.matchTitle = depthToMatchTitleSingleElim(n.depth, maxDepth, count);
        count++;
      });
    });
  }

  if (bracketType === "DoubleElimShuffle") {
    const hasGrandFinal = shuffleBracketGraphHasGrandFinal(graph);
    const { upperNodesByDepth, lowerNodesByDepth } = getDoubleElimNodesByDepth(
      !!hasGrandFinal
        ? (graph.finalNodes[0].teamSources
            .map((s) => s.node)
            .filter((n) => !!n) as ShuffleBracketNode[])
        : graph.finalNodes
    );
    const maxUpperDepth = getMaxUpperDepth(graph);
    const maxLowerDepth = getMaxLowerDepth(graph);
    let count = 1;
    upperNodesByDepth
      .reverse()
      .concat(lowerNodesByDepth.reverse())
      .forEach((d) => {
        d.forEach((n) => {
          n.matchTitle = depthToMatchTitleDoubleElim(
            n.depth,
            n.isLower ? maxLowerDepth : maxUpperDepth,
            n.isLower,
            hasGrandFinal,
            count
          );
          count++;
        });
      });

    if (hasGrandFinal) {
      const gf = graph.finalNodes[0];
      gf.matchTitle = depthToMatchTitleDoubleElim(1, 0, false, true, count);
    }
  }
};
